import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";

const emptySquadForm = {
  name: "",
  primaryColor: "#1d4ed8",
  secondaryColor: "#1e293b",
  gradient: true,
};

export default function AllStarRosterBuilder() {
  const { adminId, activeSeason } = useAdmin();
  const seasonId = activeSeason?.id;

  const [squads, setSquads] = useState([]);
  const [players, setPlayers] = useState([]);
  const [gameDate, setGameDate] = useState("");
  const [gameId, setGameId] = useState(null);
  const [roster, setRoster] = useState([]); // { player_id, team_id, original_team_id, is_substitute, substituted_for_player_id }
  const [squadForm, setSquadForm] = useState(emptySquadForm);
  const [showSquadForm, setShowSquadForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadData = useCallback(async () => {
    if (!seasonId) return;
    const [squadsRes, playersRes] = await Promise.all([
      supabase
        .from("teams")
        .select("id, name, primary_color, secondary_color, gradient")
        .eq("season_id", seasonId)
        .eq("is_all_star_team", true),
      supabase
        .from("players")
        .select("id, name, team_id, teams(name)")
        .eq("season_id", seasonId),
    ]);
    if (squadsRes.error) return setError(squadsRes.error.message);
    if (playersRes.error) return setError(playersRes.error.message);
    setSquads(squadsRes.data ?? []);
    setPlayers(playersRes.data ?? []);
  }, [seasonId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function invoke(action, extraPayload) {
    const { data, error } = await supabase.functions.invoke("admin-actions", {
      body: { action, adminId, payload: extraPayload },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  }

  async function createSquad() {
    setError(null);
    if (!squadForm.name.trim()) return setError("Squad name is required");
    try {
      await invoke("createAllStarSquad", {
        seasonId,
        name: squadForm.name,
        primaryColor: squadForm.primaryColor,
        secondaryColor: squadForm.secondaryColor,
        gradient: squadForm.gradient,
      });
      setSquadForm(emptySquadForm);
      setShowSquadForm(false);
      await loadData();
    } catch (e) {
      setError(e.message);
    }
  }

  function addToRoster(player, teamId) {
    if (roster.some((r) => r.player_id === player.id)) return; // already on the roster
    setRoster((prev) => [
      ...prev,
      {
        player_id: player.id,
        team_id: teamId,
        original_team_id: player.team_id,
        is_substitute: false,
        substituted_for_player_id: null,
      },
    ]);
  }

  function removeFromRoster(playerId) {
    setRoster((prev) => prev.filter((r) => r.player_id !== playerId));
  }

  function convertToSub(slotIndex, subPlayer) {
    setRoster((prev) =>
      prev.map((slot, i) => {
        if (i !== slotIndex) return slot;
        return {
          ...slot,
          substituted_for_player_id: slot.player_id,
          player_id: subPlayer.id,
          original_team_id: subPlayer.team_id,
          is_substitute: true,
        };
      }),
    );
  }

  async function saveAll() {
    setError(null);
    setNotice(null);
    if (squads.length < 2) return setError("Create both squads first");
    if (!gameDate) return setError("Game date is required");
    if (roster.length === 0)
      return setError("Add at least one player to the roster");

    setSaving(true);
    try {
      let currentGameId = gameId;
      if (!currentGameId) {
        const gameRes = await invoke("createAllStarGame", {
          seasonId,
          team1_id: squads[0].id,
          team2_id: squads[1].id,
          game_date: gameDate,
        });
        currentGameId = gameRes.gameId;
        setGameId(currentGameId);
      }

      await invoke("saveAllStarRoster", {
        seasonId,
        gameId: currentGameId,
        selections: roster,
      });

      setNotice(
        "Roster saved. Head to Game Results to enter stats for this game whenever you're ready.",
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const rosterByTeam = (teamId) =>
    roster
      .map((slot, index) => ({ ...slot, index }))
      .filter((slot) => slot.team_id === teamId);

  const playerById = (id) => players.find((p) => p.id === id);

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-2 rounded">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-4 py-2 rounded">
          {notice}
        </div>
      )}

      {/* Squad setup */}
      {squads.length < 2 && (
        <div className="border dark:border-gray-700 rounded-lg p-4 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {squads.length === 0
              ? "No all-star squads created yet for this season."
              : "Create the second squad to continue."}
          </p>
          {!showSquadForm ? (
            <button
              onClick={() => setShowSquadForm(true)}
              className="px-3 py-1.5 rounded bg-orange-600 text-white text-sm"
            >
              + Create Squad
            </button>
          ) : (
            <div className="flex flex-wrap gap-2 items-center">
              <input
                value={squadForm.name}
                onChange={(e) =>
                  setSquadForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Squad name (e.g. Team Fire)"
                className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700"
              />
              <input
                type="color"
                value={squadForm.primaryColor}
                onChange={(e) =>
                  setSquadForm((f) => ({ ...f, primaryColor: e.target.value }))
                }
              />
              <input
                type="color"
                value={squadForm.secondaryColor}
                onChange={(e) =>
                  setSquadForm((f) => ({
                    ...f,
                    secondaryColor: e.target.value,
                  }))
                }
              />
              <button
                onClick={createSquad}
                className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm"
              >
                Save Squad
              </button>
            </div>
          )}
        </div>
      )}

      {/* Game date */}
      {squads.length >= 2 && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Game date
          </label>
          <input
            type="date"
            value={gameDate}
            onChange={(e) => setGameDate(e.target.value)}
            className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700"
          />
        </div>
      )}

      {/* Roster builder */}
      {squads.length >= 2 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {squads.slice(0, 2).map((squad) => (
            <div
              key={squad.id}
              className="border dark:border-gray-700 rounded-lg p-4"
            >
              <h3
                className="font-bold mb-2"
                style={{ color: squad.primary_color || undefined }}
              >
                {squad.name}
              </h3>

              <div className="space-y-2 mb-3">
                {rosterByTeam(squad.id).map((slot) => {
                  const player = playerById(slot.player_id);
                  const originalPlayer = slot.substituted_for_player_id
                    ? playerById(slot.substituted_for_player_id)
                    : null;
                  return (
                    <div
                      key={slot.player_id}
                      className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-1.5"
                    >
                      <span>
                        {player?.name}
                        {player?.teams?.name ? ` (${player.teams.name})` : ""}
                        {slot.is_substitute && originalPlayer && (
                          <span className="text-amber-600 dark:text-amber-400 text-xs ml-2">
                            in for {originalPlayer.name}
                          </span>
                        )}
                      </span>
                      <div className="flex gap-2">
                        {!slot.is_substitute && (
                          <SubPicker
                            players={players}
                            excludeIds={roster.map((r) => r.player_id)}
                            onPick={(subPlayer) =>
                              convertToSub(slot.index, subPlayer)
                            }
                          />
                        )}
                        <button
                          onClick={() => removeFromRoster(slot.player_id)}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <PlayerPicker
                players={players}
                excludeIds={roster.map((r) => r.player_id)}
                onPick={(player) => addToRoster(player, squad.id)}
              />
            </div>
          ))}
        </div>
      )}

      {squads.length >= 2 && (
        <button
          onClick={saveAll}
          disabled={saving || roster.length === 0}
          className="px-4 py-2 rounded bg-orange-600 text-white disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save All-Star Roster"}
        </button>
      )}
    </div>
  );
}

function PlayerPicker({ players, excludeIds, onPick }) {
  const [query, setQuery] = useState("");
  const filtered = players
    .filter((p) => !excludeIds.includes(p.id))
    .filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 8);

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Add a player…"
        className="w-full border dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700"
      />
      {query && (
        <div className="mt-1 border dark:border-gray-600 rounded max-h-40 overflow-y-auto">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                onPick(p);
                setQuery("");
              }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {p.name} {p.teams?.name ? `(${p.teams.name})` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SubPicker({ players, excludeIds, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = players
    .filter((p) => !excludeIds.includes(p.id))
    .filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 8);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs text-blue-600 dark:text-blue-400"
      >
        Mark sub
      </button>
    );
  }

  return (
    <div className="relative">
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Who subbed in?"
        className="border dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700"
      />
      {query && (
        <div className="absolute z-10 mt-1 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 max-h-32 overflow-y-auto">
          {filtered.map((p) => (
            <button
              key={p.id}
              onMouseDown={() => {
                onPick(p);
                setOpen(false);
                setQuery("");
              }}
              className="block w-full text-left px-3 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {p.name} {p.teams?.name ? `(${p.teams.name})` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
